import { AxiosResponse } from "axios";
import {
  AnalyticsWebDownloadRateParams,
  CancelAttemptResult,
  WebSignUpParamsAnalytics,
} from "../../Common/models/analytics";
import {
  doDelete,
  doGet,
  doPost,
  encodeQueryData,
} from "../../Common/utils/https";
import { getRequestHeaders } from "../../Common/utils/https";
import { cloudFunctionsUrl } from "../firebase";
import { WLStripeSubscription } from "../../Common/models/stripe";
const baseUrl = cloudFunctionsUrl;

export const validateExistingUser = async (emailAddress: string) => {
  const url = `${baseUrl}users/validateExistingUser?${encodeQueryData({
    emailAddress,
  })}`;
  const headers = await getRequestHeaders();
  return doGet(url, headers);
};

export const getUserHashedDetails = async (userId: string) => {
  const url = `${baseUrl}users/getUserHashedDetails?${encodeQueryData({
    userId,
  })}`;
  const headers = await getRequestHeaders();
  return doGet(url, headers);
};

export const trackWebSignUpSteps = async (data: WebSignUpParamsAnalytics) => {
  const url = `${baseUrl}users/trackWebSignUpSteps`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const trackWebDownloadRates = async (
  data: AnalyticsWebDownloadRateParams,
) => {
  const url = `${baseUrl}users/trackWebDownloadRates`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const createWebSignupAppHandoff = async (data: {
  userId: string;
  emailAddress: string;
}): Promise<
  AxiosResponse<{
    branchUrl: string;
    maskedPhone: string;
    expiresAt: number;
  }>
> => {
  const url = `${baseUrl}users/createWebSignupAppHandoff`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const trackSignUpCheckoutMethod = async (data: {
  paymentIntentClientSecret: string;
  subscription: string;
  emailAddress: string;
  revenue: number;
  promoCode: string | null;
  express: boolean;
}) => {
  const url = `${baseUrl}users/trackSignUpCheckoutMethod`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const trackUserReferral = async (data: {
  referralMemberId: string;
  wlReferralConfigLookupKey: string;
  referredEmailAddress: string; // give this is required, you must only trigger this once email is set, but before user even clicks on submit. Will serve as a "View"
  revenue: number;
}) => {
  const url = `${baseUrl}users/trackUserReferral`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const sendEmail = async (data: {
  userId: string;
  subject: string;
  html: string;
}) => {
  const url = `${baseUrl}users/sendEmail`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const sendCompleteProfileBackupEmail = async (data: {
  userId: string;
  returnUrl: string;
  paymentIntentId: string;
  firstName?: string;
}) => {
  const url = `${baseUrl}users/sendCompleteProfileBackupEmail`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const completeProfile = async (data: {
  userId: string;
  emailAddress: string;
  password: string;
  phoneNumber: string;
  residentialState: string;
  firstName: string;
  lastName: string;
}) => {
  const url = `${baseUrl}users/completeProfile`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const getUserTokenRequest = async (data: {
  userId: string;
}): Promise<AxiosResponse<{ valid: boolean; token?: string }>> => {
  const url = `${baseUrl}users/getUserTokenRequest`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const validateSignInWeb = async (data: {
  userId: string;
}): Promise<
  AxiosResponse<{
    valid: boolean;
    token?: string;
    phoneNumber?: string;
    firstName?: string;
  }>
> => {
  const url = `${baseUrl}users/validateSignInWeb`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const verifyWebOtpLogin = async (data: {
  userId: string;
  phoneNumber: string;
  code: string;
}): Promise<AxiosResponse<{ valid: boolean; message?: string }>> => {
  const url = `${baseUrl}users/verifyWebOtpLogin`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, data);
};

export const trackUserOfferedFixBilling = async (data: {
  userId: string;
  promoCode: string;
  revenue: number;
  offerViewedAt: "app" | "web" | "redirectFromApp" | "drawDayWebFlow" | "admin";
}) => {
  const url = `${baseUrl}users/trackUserOfferedFixBilling`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const trackCancelResult = async (data: {
  userId: string;
  result: CancelAttemptResult;
  source: "web";
  cancelFlowVersion?: "v3" | "v4";
}) => {
  const url = `${baseUrl}users/trackCancelResult`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const claimPowerUpFromCancelling = async (data: {
  userId: string;
  giveawayId: string;
}) => {
  const url = `${baseUrl}users/claimPowerUpFromCancelling`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const getUserPowerUps = async (userId: string) => {
  const url = `${baseUrl}users/getUserPowerUps?${encodeQueryData({
    userId,
  })}`;
  const headers = await getRequestHeaders(true);
  return doGet(url, headers);
};

export const deleteUser = async ({ userId }: { userId: string }) => {
  const url = `${baseUrl}users/deleteUser?${encodeQueryData({
    userId,
  })}`;
  const headers = await getRequestHeaders(true);
  return doDelete(url, headers);
};

export const clearUserTemporaryToken = async (data: { userId: string }) => {
  const url = `${baseUrl}users/clearUserTemporaryToken`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const getClientIpAndUserAgent = async (): Promise<
  AxiosResponse<{ ipAddress: string | null; userAgent: string | null }>
> => {
  const url = `${baseUrl}users/getClientIpAndUserAgent`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, {});
};
